import type { Scope } from '@gql'
import { CommonScopeCode } from '@gql'
import type { FormErrorFieldMap } from '@hooks/useForm'
import { GraphQLErrorCode } from '@sections/auth/errors'
import { findValueInEnum } from '@utils/enum'
import type { GraphQLFormattedError } from 'graphql'
import { useEffect } from 'react'
import type { FieldValues, UseFormReturn } from 'react-hook-form'

interface Error<TScopeCode> {
  fields: string[]
  message: string
  scope: Scope
  scopeCode: keyof TScopeCode | keyof CommonScopeCode
}

export function getValidationErrors<TScopeCode extends Record<string, any>>(
  errors: readonly GraphQLFormattedError[],
  type: TScopeCode | null,
): Array<Error<TScopeCode>> {
  return errors
    .map((err) => {
      if (err.extensions?.code !== GraphQLErrorCode.InputValidationFailed) {
        return null
      }

      return {
        fields: err.extensions.field,
        message: err.message,
        scope: err.extensions.scope as Scope,
        scopeCode:
          err.extensions.scopeCode && type !== null
            ? findValueInEnumOrCommon(err.extensions.scopeCode as string, type)
            : CommonScopeCode.INVALID,
      }
    })
    .filter(Boolean) as Array<Error<TScopeCode>>
}

function useServerFormHooks<
  TScopeCode extends Record<string, any>,
  TFieldValues extends FieldValues,
>(
  codes: TScopeCode | null,
  errors: readonly GraphQLFormattedError[] | null,
  errorFieldMap: FormErrorFieldMap<TFieldValues> | null,
  formMethods: Pick<UseFormReturn<any>, 'setError'>,
) {
  const { setError } = formMethods
  useEffect(() => {
    if (errors) {
      const errs = getValidationErrors(errors, codes)

      errs.forEach((err) => {
        if (err.fields) {
          const normalizedFields = Array.isArray(err.fields) ? err.fields : [err.fields]
          normalizedFields.map((field) => {
            if (field) {
              const formField = errorFieldMap?.[field] ?? field
              setError(
                formField,
                {
                  message: err.message,
                  type: 'server',
                },
                {
                  shouldFocus: true,
                },
              )
            }
          })
        }
      })
    }
  }, [errors, codes, errorFieldMap, setError])
}

function findValueInEnumOrCommon<TEnum extends Record<string, any>>(
  scopeCode: string,
  type: TEnum,
): TEnum[keyof TEnum] | CommonScopeCode[keyof CommonScopeCode] {
  try {
    return findValueInEnum(scopeCode as string, type)
  } catch (_) {
    try {
      return findValueInEnum(scopeCode as string, CommonScopeCode)
    } catch (__) {
      return CommonScopeCode.INVALID
    }
  }
}

export default useServerFormHooks
